import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { toast } from "react-toastify";
import { Oval } from "react-loader-spinner"; // Import loader
import { useNavigate } from "react-router-dom";
import { RoleGuard } from "../App";
import { StandardURL } from "../App";

const TransactionsViewPage = () => {
  const propertyId = localStorage.getItem("selectedPropertyId");
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTransactions = () => {
      setIsLoading(true); // Set loading state to true
      const token = localStorage.getItem("token");
      axios
        .get(
          `${StandardURL}/iNyumba/alltransactionsviewthisyear/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setTransactions(response.data.response); // Assuming the response structure
          setIsLoading(false); // Set loading state to false
        })
        .catch((error) => {
          console.error("Error fetching transactions:", error);
          toast.error(
            error.response?.data?.response || "Error fetching transactions"
          );
          setIsLoading(false); // Set loading state to false
        });
    };

    fetchTransactions(); // Call the function immediately after component mounts
  }, [propertyId]); // Dependency array includes propertyId

  const handleDownloadCSV = () => {
    const token = localStorage.getItem("token");
    axios
      .get(
        `${StandardURL}/iNyumba/reportsdownloadtransactions/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob", // Important for file download
        }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "inyumbatransactions.csv");
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        console.error("Error downloading report:", error);
        toast.error("Failed to download the report.");
      });
  };

  const handleArchivedData = () => {
    navigate("/landlord/archiveddatatransactions");
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200">
            {/* --- Header Section --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight flex items-center">
                Transaction History 💸
              </h1>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleDownloadCSV}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-300 ease-in-out transform hover:-translate-y-0.5"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Download Report
                </button>
                <button
                  onClick={handleArchivedData}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition duration-300 ease-in-out transform hover:-translate-y-0.5"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" />
                    <path
                      fillRule="evenodd"
                      d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  View Archived
                </button>
              </div>
            </div>

            <p className="text-gray-600 mb-8 max-w-3xl">
              This page provides a comprehensive overview of all financial transactions. You can download a CSV report for record-keeping or view historical archived data.
            </p>

            {/* --- Loading State --- */}
            {isLoading ? (
              <div className="flex justify-center items-center py-10">
                <Oval
                  height={50}
                  width={50}
                  color="#4F46E5" // A nice indigo color
                  ariaLabel="loading-indicator"
                  secondaryColor="#E0E7FF" // Lighter shade for spinner background
                  strokeWidth={5}
                  strokeWidthSecondary={5}
                />
                <p className="ml-4 text-lg text-gray-700 font-medium">Loading transactions...</p>
              </div>
            ) : (
              /* --- Transactions Table --- */
              <div className="overflow-x-auto relative shadow-lg rounded-lg border border-gray-100">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-teal-600 to-green-700 text-white">
                    <tr>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider rounded-tl-lg">Date</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Transaction ID</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wider">Channel</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wider">Amount</th>
                      <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wider rounded-tr-lg">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {transactions.length > 0 ? (
                      transactions.map((transaction) => (
                        <tr
                          key={transaction.transaction_id}
                          className="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
                        >
                          <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {new Date(transaction.created_at).toLocaleDateString("en-KE")}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-700">
                            <span className="font-mono bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">
                                {transaction.transaction_id}
                            </span>
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-700">
                            <span className="font-semibold text-blue-700">
                                {transaction.reference}
                            </span>
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-right font-bold text-green-600">
                            Ksh {parseFloat(transaction.amount).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-right font-bold text-indigo-700">
                            Ksh {parseFloat(transaction.running_balance).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="py-8 px-4 text-center text-lg text-gray-500 italic">
                          <div className="flex flex-col items-center justify-center">
                            <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                            No transactions found.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default TransactionsViewPage;
